import {
  type NavigationConfigStaticParentAndChildUnionType,
  type IMergedStaticAndDbConfigItem,
  type INavigationConfigDBItem,
} from 'views/Settings/NavigationMap/types/types';
import { staticNavigationMap } from './staticNavigationMap';

export type AppModuleKey = 'machines' | 'parts' | 'tires';

export const normalizeNavModule = (
  module?: string | null
): AppModuleKey | null => {
  const trimmed = (module ?? '').trim();
  if (!trimmed) return null;
  const normalized = trimmed.toLowerCase();

  if (normalized === 'machines' || normalized === 'xmachine') return 'machines';
  if (normalized === 'parts' || normalized === 'xparts') return 'parts';
  if (normalized === 'tires' || normalized === 'xtire') return 'tires';

  // Unknown module => treat as common (avoid accidentally hiding routes)
  return null;
};

export const toDbNavModule = (module: AppModuleKey | null): string => {
  if (!module) return '';
  if (module === 'machines') return 'xmachine';
  if (module === 'parts') return 'xparts';
  if (module === 'tires') return 'xtire';
  return '';
};

export const hasRequiredRole = (
  userRoles: string[] = [],
  requiredRoles: string[] = []
): boolean => {
  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }
  return userRoles.some((role) => requiredRoles.includes(role));
};

export const isExcluded = (excludeList?: string[], customerCode?: string) => {
  return excludeList?.includes(customerCode ?? '');
};

export const isInEnabled = (enabledList?: string[], customerCode?: string) => {
  return enabledList?.includes(customerCode ?? '');
};

/**
 * Determines if a route should be enabled in the current environment
 * @param enabledEnvironments - List of environments the route is enabled for
 * @returns boolean - Whether the route should be visible in the current environment
 */
export const isEnabledForEnvironment = (
  enabledEnvironments?: Array<'development' | 'staging' | 'production'>
) => {
  // If no environments specified, enable for all environments
  if (!enabledEnvironments || enabledEnvironments.length === 0) {
    return true;
  }

  // Get the current environment from NODE_ENV
  const currentEnv = process.env.REACT_APP_ENV ?? 'development';

  // Check if current environment is in the enabled environments
  return enabledEnvironments.includes(
    currentEnv as 'development' | 'staging' | 'production'
  );
};

// merged static nav with db config for navigations

export const mergeNavigationWithDbConfig = (
  databaseNavConfigData: INavigationConfigDBItem[]
) => {
  const databaseItemById = new Map(
    databaseNavConfigData.map((item) => [item.id, item])
  );

  const mergeItem = (
    staticItem: NavigationConfigStaticParentAndChildUnionType,
    inheritedModule: AppModuleKey | null = null
  ) => {
    const databaseItem = databaseItemById.get(staticItem.id);

    if (!databaseItem) {
      return null;
    }

    if (!databaseItem.webEnabled) {
      // do not add if  not enabled on web
      return null;
    }

    const itemModule =
      normalizeNavModule(databaseItem.module) ?? inheritedModule;

    const mergedChildren = staticItem.children
      ?.map((child) => mergeItem(child, itemModule))
      .filter(Boolean) as IMergedStaticAndDbConfigItem[] | undefined;

    const item: IMergedStaticAndDbConfigItem = {
      id: staticItem.id,
      icon: staticItem.icon,
      component: staticItem.component,

      // dynamic
      path: databaseItem.path || staticItem.path,
      label: databaseItem.label,
      roles: databaseItem.roles,
      enabledEnvironments: databaseItem.environments,
      invisible: databaseItem.invisible,
      enabledFor: [databaseItem.customerCode],
      children: mergedChildren?.length ? mergedChildren : undefined,
      showPro: databaseItem.showPro ?? false,
      module: itemModule,
    };

    return item;
  };

  return staticNavigationMap
    .map((item) => mergeItem(item))
    .filter(Boolean) as IMergedStaticAndDbConfigItem[];
};
